import type { OrgDirectoryMember } from "../../../../@types/webotServer";

/**
 * WHOSE FACE TO PAINT FOR A COLLEAGUE — the org directory first, the row's own stamp only
 * as a fallback.
 *
 * ── The two sources are not equally good, and the difference is measurable ──────
 *
 * A contact row and a holder row both carry `ownerName` / `ownerAvatarUrl`, and both come
 * from `ownersData[0]` ON THE CONTACT DOCUMENT — stamped by `addContactRealtorAndOrgFields`
 * at the time that contact was last written. So they are a SNAPSHOT of a colleague as they
 * were when the record was touched: absent entirely on anything written before that
 * function existed, and stale for anyone who has changed their photo since.
 *
 * `useOrgDirectory` reads the same expression (`photoURL || userImageUrl`) LIVE off the
 * user document, once per session, for the whole agency. It is therefore the better answer
 * whenever it has one — and it is already in memory on this page, so preferring it costs
 * nothing.
 *
 * ── Why a shared helper rather than the choice being made per component ─────────
 *
 * Three surfaces paint the same colleague within one row: the avatar stack, the expansion
 * panel it opens, and the agency view's `מתווך` column. If any one of them resolved
 * differently, the same person would appear with a photo in one and initials in another,
 * on the SAME row — which reads as two different people, not as a missing image.
 *
 * ── Fallbacks are chained per FIELD, not per source ─────────────────────────────
 *
 * A directory entry with a name but no photo is the common shape (a colleague who never
 * uploaded one), so falling back wholesale to the stamped copy would throw away the fresh
 * name to recover an avatar that is probably also absent. Each field takes the first thing
 * it can get.
 *
 * `null`, never `""` — `<Avatar src="">` renders a broken image, whereas an absent `src`
 * falls back to the initials the caller passes as children.
 */
export type CrmMemberFace = {
  /** `null` when nobody knows — the CALLER decides whether to print the email. */
  name: string | null;
  avatarUrl: string | null;
};

export const crmMemberFace = (
  member: OrgDirectoryMember | null | undefined,
  fallbackName?: string | null,
  fallbackAvatarUrl?: string | null
): CrmMemberFace => ({
  name: member?.displayName?.trim() || fallbackName?.trim() || null,
  avatarUrl: member?.avatarUrl || fallbackAvatarUrl || null
});
